// src/pages/admin/Dashboard.tsx
//
// Admin dashboard. KPIs are pulled live from /api/admin/overview
// (real imported FileMaker ledger + portal orders) and the repairs
// widget reads the live /api/repairs backend. The recent approvals
// list still reads from demo data because there is no approval store
// wired to the backend yet.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Package,
  Users,
  ShoppingBag,
  TrendingUp,
  Euro,
  ArrowUpRight,
  Wrench,
  Megaphone,
  Receipt,
  RefreshCcw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DEMO_APPROVALS, DEMO_PARTNER_SHOPS, DEMO_PRODUCTS } from "@/lib/demoData";
import { fetchRepairs, type RepairTicketRow } from "@/lib/opsdesk/api";
import { isFeatureEnabled } from "@/lib/featureFlags";
import { useAuth } from "@/components/auth/AuthContext";
import { authHeaders } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";
import { FilemakerSyncDashboardWidget } from "./FilemakerSync";
import { InvoicedSalesCard } from "./InvoicedSalesCard";

interface Overview {
  as_of: string;
  totals: {
    products: number; customers: number; suppliers: number;
    invoices: number; invoice_lines: number;
    lifetime_htva: string; lifetime_tvac: string;
  };
  ytd: { invoices: number; htva: string };
  month: { invoices: number; htva: string };
  last7: { invoices: number; htva: string };
  orders: { new: number; confirmed: number; in_fulfilment: number };
  top_customer_ytd: { customer_id: number; name: string; invoices: number; htva: number } | null;
  top_product_ytd: { product_id: number; label: string; qty: number; htva: number } | null;
}

export function AdminDashboard() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [overviewError, setOverviewError] = useState("");
  const [repairs, setRepairs] = useState<RepairTicketRow[]>([]);

  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    setOverviewError("");
    fetch("/api/admin/overview", { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setOverview((await r.json()) as Overview);
      })
      .catch((e) => setOverviewError(String(e instanceof Error ? e.message : e)));
    // Live repairs for the glance widget (separate failure domain: a
    // repairs outage must not blank the KPI grid).
    fetchRepairs()
      .then((body) => setRepairs(body.rows))
      .catch(() => setRepairs([]));
  }, [auth.isReady, auth.isAuthenticated]);

  // Real KPIs from /api/admin/overview. Pending approvals still read
  // from the demo store because there is no approval backend yet.
  const liveKpis = overview
    ? [
        {
          label: t("admin.dashboard.productsInCatalog"),
          value: overview.totals.products.toLocaleString(),
          delta: t("admin.dashboard.fromFileMakerImport"),
          icon: Package,
          accent: "indigo" as const,
        },
        {
          label: t("admin.dashboard.customers"),
          value: overview.totals.customers.toLocaleString(),
          delta: t("admin.dashboard.suppliers", {
            count: overview.totals.suppliers,
          }),
          icon: Users,
          accent: "teal" as const,
        },
        {
          label: t("admin.dashboard.invoicesYear", {
            year: new Date(overview.as_of).getFullYear(),
          }),
          value: overview.ytd.invoices.toLocaleString(),
          delta: t("admin.dashboard.lifetime", {
            value: overview.totals.invoices.toLocaleString(),
          }),
          icon: Receipt,
          accent: "amber" as const,
        },
        {
          label: t("admin.dashboard.revenueHtvaLifetime"),
          value: formatPrice(Number(overview.totals.lifetime_htva), "EUR"),
          delta: t("admin.dashboard.ytd", {
            value: formatPrice(Number(overview.ytd.htva), "EUR"),
          }),
          icon: Euro,
          accent: "green" as const,
        },
      ]
    : null;

  // Pending portal work (orders to review / confirm / pack / ship).
  const orders = overview?.orders;
  // (Recently approved and pending items come from the demo store
  // because there is no approval backend yet.)
  const liveRepairs = repairs.filter((tk) => tk.status !== "closed");
  const waitingOnHuman = liveRepairs.filter(
    (tk) => tk.status === "triage" || tk.status === "in-repair",
  );
  const vendorRouting = liveRepairs.filter(
    (tk) => tk.status === "vendor-routing",
  );
  const approvedProducts = DEMO_PRODUCTS.filter((p) => p.approved === "approved");
  const totalProducts = approvedProducts.length;

  // Fail-closed feature flags (M2 A21): demo-backed widgets below are
  // hidden unless their surface is explicitly enabled at build time.
  // Live widgets (KPI grid, portal work queue, repairs) are unaffected.
  const showApprovals = isFeatureEnabled("adminApprovals");
  const showProducts = isFeatureEnabled("adminProducts");
  const showCampaigns = isFeatureEnabled("adminCampaigns");
  const showFilemakerSync = isFeatureEnabled("adminFilemakerSync");

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
          {t("admin.dashboard.title")}
        </h1>
        <p className="mt-1 text-sm text-fg-tertiary">
          {t("admin.dashboard.subtitle")}
        </p>
      </div>

      {/* Live KPI grid (FileMaker ledger + portal orders) */}
      {liveKpis ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {liveKpis.map((kpi) => (
            <Card key={kpi.label} className="overflow-hidden">
              <CardContent className="space-y-2 p-5">
                <div className="flex items-start justify-between">
                  <div
                    className={
                      "flex h-9 w-9 items-center justify-center rounded ring-1 " +
                      {
                        indigo: "bg-accent-indigo/15 ring-accent-indigo/30",
                        teal: "bg-benchmark-teal/15 ring-benchmark-teal/30",
                        amber: "bg-accent-amber/15 ring-accent-amber/30",
                        green: "bg-accent-green/15 ring-accent-green/30",
                      }[kpi.accent]
                    }
                  >
                    <kpi.icon
                      className={
                        "h-4 w-4 " +
                        {
                          indigo: "text-accent-indigo",
                          teal: "text-benchmark-teal-bright",
                          amber: "text-accent-amber",
                          green: "text-accent-green",
                        }[kpi.accent]
                      }
                      strokeWidth={1.5}
                    />
                  </div>
                  {kpi.delta && (
                    <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
                      {kpi.delta}
                    </span>
                  )}
                </div>
                <div className="text-3xl font-510 tracking-tighter text-fg-primary">
                  {kpi.value}
                </div>
                <div className="text-xs text-fg-tertiary">{kpi.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-border bg-bg-canvas/30 p-3 text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-3.5 w-3.5 animate-spin" />
          {t("admin.dashboard.loadingLive")}
          {overviewError ? ` (${overviewError})` : ""}
        </div>
      )}

      {/* Live portal work queue (orders to review / confirm / fulfil) */}
      {orders && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t("admin.dashboard.portalWorkQueue")}</CardTitle>
              <CardDescription>
                {t("admin.dashboard.portalWorkQueueDescription")}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" href="/admin/orders">
              {t("admin.dashboard.openQueue")}
              <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-3">
              <Mini
                label={t("admin.dashboard.awaitingConfirm")}
                value={orders.new}
                to="/admin/orders"
                accent="amber"
              />
              <Mini
                label={t("admin.dashboard.confirmedToFulfil")}
                value={orders.confirmed}
                to="/admin/orders"
                accent="teal"
              />
              <Mini
                label={t("admin.dashboard.inFulfilment")}
                value={orders.in_fulfilment}
                to="/admin/orders"
                accent="indigo"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Latest invoiced sales from the real FileMaker ledger — the
          queue above is empty until customers submit Shop order
          requests; historic sales activity lives here (read-only). */}
      <InvoicedSalesCard limit={8} />

      {/* Two-column section: Recent approvals + Quick stats (demo-backed,
          each half hidden unless its feature flag is enabled) */}
      {(showApprovals || showProducts) && (
      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        {/* Recent approval activity */}
        {showApprovals && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle>{t("admin.dashboard.recentApprovalActivity")}</CardTitle>
              <CardDescription>
                {t("admin.dashboard.recentApprovalDescription")}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" href="/admin/approvals">
              {t("admin.dashboard.viewAll")}
              <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
            </Button>
          </CardHeader>
          <CardContent className="divide-y divide-border p-0">
            {DEMO_APPROVALS.slice(0, 4).map((item) => (
              <Link
                key={item.id}
                to="/admin/approvals"
                className="flex items-start justify-between gap-3 px-5 py-3 transition-colors hover:bg-bg-elevated"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        item.risk === "low"
                          ? "success"
                          : item.risk === "medium"
                            ? "warning"
                            : "danger"
                      }
                      size="sm"
                    >
                      {item.risk}
                    </Badge>
                    <Badge variant="default" size="sm">
                      {item.type}
                    </Badge>
                    <span className="font-mono text-[10px] text-fg-quaternary">
                      {item.id}
                    </span>
                  </div>
                  <div className="mt-1 truncate text-sm font-510 text-fg-primary">
                    {item.title}
                  </div>
                  <div className="mt-0.5 line-clamp-1 text-xs text-fg-tertiary">
                    {item.summary}
                  </div>
                  <div className="mt-1 text-[11px] text-fg-quaternary">
                    by {item.submittedBy} · {new Date(item.submittedAt).toLocaleDateString()}
                  </div>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
        )}

        {/* Top categories */}
        {showProducts && (
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.dashboard.catalogByCategory")}</CardTitle>
            <CardDescription>{t("admin.dashboard.distributionOfProducts")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <CategoryBar category="video" label={t("admin.dashboard.categoryVideo")} value={DEMO_PRODUCTS.filter((p) => p.category === "video").length} max={totalProducts} />
            <CategoryBar category="audio" label={t("admin.dashboard.categoryAudio")} value={DEMO_PRODUCTS.filter((p) => p.category === "audio").length} max={totalProducts} />
            <CategoryBar category="screens" label={t("admin.dashboard.categoryScreens")} value={DEMO_PRODUCTS.filter((p) => p.category === "screens").length} max={totalProducts} />
            <CategoryBar category="acoustic" label={t("admin.dashboard.categoryAcoustic")} value={DEMO_PRODUCTS.filter((p) => p.category === "acoustic").length} max={totalProducts} />
            <CategoryBar category="accessories" label={t("admin.dashboard.categoryAccessories")} value={DEMO_PRODUCTS.filter((p) => p.category === "accessories").length} max={totalProducts} />
          </CardContent>
        </Card>
        )}
      </div>
      )}

      {/* Repairs at a glance (LIVE /api/repairs — always rendered) +
          Marketing campaigns trigger (demo-backed, flag-gated) */}
      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        {/* Repairs glance */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-benchmark-teal-bright" strokeWidth={1.5} />
                {t("admin.dashboard.repairsAtAGlance")}
              </CardTitle>
              <CardDescription>{t("admin.dashboard.repairsDescription")}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" href="/admin/repairs">
              {t("admin.dashboard.openQueue")}
              <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {liveRepairs.length === 0 ? (
              <p className="text-xs text-fg-quaternary">
                {t("admin.dashboard.noOpenRepairs")}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <RepairStat
                    label={t("admin.dashboard.statLive")}
                    value={liveRepairs.length}
                    tone="info"
                  />
                  <RepairStat
                    label={t("admin.dashboard.statVendorRoute")}
                    value={vendorRouting.length}
                    tone="indigo"
                  />
                  <RepairStat
                    label={t("admin.dashboard.statNeedsHuman")}
                    value={waitingOnHuman.length}
                    tone="amber"
                  />
                </div>
                <div className="space-y-1.5 border-t border-border pt-3">
                  {liveRepairs.slice(0, 4).map((tk) => (
                    <Link
                      key={tk.ticket_id}
                      to="/admin/repairs"
                      className="flex items-center justify-between gap-3 rounded border border-border bg-bg-canvas/40 p-2 text-xs transition-colors hover:border-benchmark-teal/40"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px] text-fg-quaternary">
                            {tk.ticket_id}
                          </span>
                          <span className="truncate text-fg-secondary">
                            {tk.customer_name}
                          </span>
                        </div>
                        <div className="truncate text-fg-tertiary">
                          {tk.subject}
                        </div>
                      </div>
                      <Badge
                        variant={
                          tk.status === "closed" ? "default" : "warning"
                        }
                        size="sm"
                        className="shrink-0"
                      >
                        {tk.status.replace("-", " ")}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Marketing campaigns launcher */}
        {showCampaigns && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Megaphone
                  className="h-4 w-4 text-accent-indigo"
                  strokeWidth={1.5}
                />
                {t("admin.dashboard.campaignsTitle")}
              </CardTitle>
              <CardDescription>{t("admin.dashboard.campaignsDescription")}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" href="/admin/campaigns">
              {t("admin.dashboard.openCampaigns")}
              <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-fg-tertiary">
              {t("admin.dashboard.campaignsBody")}
            </p>
            <ul className="space-y-1 text-xs text-fg-secondary">
              <li className="flex items-center justify-between">
                <span>{t("admin.dashboard.targetableTiers")}</span>
                <span className="font-mono text-[10px] text-fg-quaternary">3</span>
              </li>
              <li className="flex items-center justify-between">
                <span>{t("admin.dashboard.playTemplatesAvailable")}</span>
                <span className="font-mono text-[10px] text-fg-quaternary">12</span>
              </li>
              <li className="flex items-center justify-between">
                <span>{t("admin.dashboard.reachSubset")}</span>
                <span className="font-mono text-[10px] text-fg-quaternary">
                  {DEMO_PARTNER_SHOPS.length}
                </span>
              </li>
            </ul>
            <div className="border-t border-border pt-3 text-[10px] uppercase tracking-wider text-fg-quaternary">
              {t("admin.dashboard.hitlFooter")}
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* FileMaker sync status (demo mirror state — flag-gated) */}
      {showFilemakerSync && (
      <div className="grid gap-6 lg:grid-cols-[1fr]">
        <FilemakerSyncDashboardWidget />
      </div>
      )}

      {/* Welcome / capabilities */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-benchmark-teal-bright" strokeWidth={1.5} />
            {t("admin.dashboard.whatYouCanDo")}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {showApprovals && (
            <CardCapability
              title={t("admin.dashboard.reviewApprovals")}
              description={t("admin.dashboard.reviewApprovalsDescription")}
              link="/admin/approvals"
            />
          )}
          {showProducts && (
            <CardCapability
              title={t("admin.dashboard.manageProducts")}
              description={t("admin.dashboard.manageProductsDescription")}
              link="/admin/products"
            />
          )}
          <CardCapability
            title={t("admin.dashboard.customerDirectory")}
            description={t("admin.dashboard.customerDirectoryDescription")}
            link="/admin/customers"
          />
          {showCampaigns && (
            <CardCapability
              title={t("admin.dashboard.runCampaign")}
              description={t("admin.dashboard.runCampaignDescription")}
              link="/admin/campaigns"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- inline components ----------

interface CategoryBarProps {
  category: string;
  label: string;
  value: number;
  max: number;
}
function CategoryBar({ category, label, value, max }: CategoryBarProps) {
  const { t } = useTranslation();
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="text-fg-secondary">
          <ShoppingBag className="mr-1 inline h-3 w-3 text-fg-tertiary" strokeWidth={1.5} />
          {label}
        </span>
        <span className="text-fg-tertiary">
          {t("admin.dashboard.itemsCount", { count: value, pct })}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded bg-bg-elevated">
        <div
          className="h-full bg-benchmark-teal/60 transition-all"
          style={{ width: `${pct}%` }}
          data-category={category}
        />
      </div>
    </div>
  );
}

function CardCapability({ title, description, link }: { title: string; description: string; link: string }) {
  return (
    <Link
      to={link}
      className="group rounded-md border border-border bg-bg-surface p-4 transition-colors hover:border-benchmark-teal/40"
    >
      <div className="text-sm font-510 text-fg-primary group-hover:text-benchmark-teal-bright">
        {title}
      </div>
      <div className="mt-1 line-clamp-2 text-xs text-fg-tertiary">
        {description}
      </div>
    </Link>
  );
}

function RepairStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "info" | "indigo" | "amber";
}) {
  const toneClass = {
    info: "bg-accent-info/15 text-accent-info",
    indigo: "bg-accent-indigo/15 text-accent-indigo",
    amber: "bg-accent-amber/15 text-accent-amber",
  }[tone];
  return (
    <div
      className={`rounded-md p-2 text-center ${toneClass}`}
      data-tone={tone}
    >
      <div className="text-xl font-510 leading-tight">{value}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}

function Mini({
  label, value, to, accent,
}: {
  label: string;
  value: number;
  to: string;
  accent: "amber" | "teal" | "indigo";
}) {
  const cls = {
    amber: "border-accent-amber/30 bg-accent-amber/5 text-accent-amber",
    teal: "border-benchmark-teal/30 bg-benchmark-teal/5 text-benchmark-teal-bright",
    indigo: "border-accent-indigo/30 bg-accent-indigo/5 text-accent-indigo",
  }[accent];
  return (
    <Link
      to={to}
      className={
        "rounded-md border px-3 py-2 transition-colors hover:border-fg-tertiary " + cls
      }
    >
      <div className="text-2xl font-510 tracking-tighter text-fg-primary">
        {value.toLocaleString()}
      </div>
      <div className="text-xs text-fg-tertiary">{label}</div>
    </Link>
  );
}
